import { type ReactNode, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { SitStatDragHandle } from './SitStatDragHandle';
import { SitStatRailsShell } from './SitStatRailsShell';
import { SitStatWidgetCard } from './SitStatWidgetCard';
import { getUnusedSitStatWidgets } from './sitstat-layout-storage';
import type { SitStatTemplate } from './sitstat-template-types';
import {
  SITSTAT_WIDGET_TITLES,
  isSitStatRailColumnId,
  isSitStatWidgetId,
  type SitStatLayout,
  type SitStatWidgetId,
} from './sitstat-widget-types';
import { useAorText } from 'utils/aorTerminology';

type RailId = 'left' | 'right';

type SitStatSortableGridProps = {
  layout: SitStatLayout;
  onLayoutChange: (layout: SitStatLayout) => void;
  leftCollapsed: boolean;
  rightCollapsed: boolean;
  onLeftCollapsedChange: (collapsed: boolean) => void;
  onRightCollapsedChange: (collapsed: boolean) => void;
  onExitSitStat?: () => void;
  renderWidget: (id: SitStatWidgetId) => ReactNode;
  templates?: SitStatTemplate[];
  activeTemplateId?: string | null;
  onLoadTemplate?: (template: SitStatTemplate) => void;
  onSaveTemplate?: (name: string) => Promise<void> | void;
  mapSlot: ReactNode;
  className?: string;
};

function findRail(layout: SitStatLayout, id: string): RailId | null {
  if (isSitStatRailColumnId(id)) return id;
  if (!isSitStatWidgetId(id)) return null;
  if (layout.left.includes(id)) return 'left';
  if (layout.right.includes(id)) return 'right';
  return null;
}

function paletteDragId(widgetId: SitStatWidgetId): string {
  return `palette:${widgetId}`;
}

function parsePaletteDragId(id: string): SitStatWidgetId | null {
  if (!id.startsWith('palette:')) return null;
  const widgetId = id.slice('palette:'.length);
  return isSitStatWidgetId(widgetId) ? widgetId : null;
}

function SortableSitStatWidget({
  id,
  children,
  onRemove,
  isOverlay = false,
}: {
  id: SitStatWidgetId;
  children: ReactNode;
  onRemove?: () => void;
  isOverlay?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const aorText = useAorText();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        isDragging && !isOverlay && 'tw:opacity-40',
        isOverlay && 'tw:shadow-lg'
      )}
    >
      <SitStatWidgetCard
        title={aorText(SITSTAT_WIDGET_TITLES[id])}
        onRemove={onRemove}
        tour={isOverlay ? undefined : `sitstat-widget-${id}`}
        dragHandle={
          <SitStatDragHandle
            ariaLabel={`Reorder ${aorText(SITSTAT_WIDGET_TITLES[id])}`}
            isDragging={isDragging || isOverlay}
            setActivatorNodeRef={setActivatorNodeRef}
            attributes={attributes}
            listeners={listeners}
          />
        }
      >
        {children}
      </SitStatWidgetCard>
    </div>
  );
}

function PaletteDraggableItem({ id }: { id: SitStatWidgetId }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: paletteDragId(id),
  });
  const aorText = useAorText();
  return (
    <button
      ref={setNodeRef}
      type="button"
      className={cn(
        'tw:flex tw:w-full tw:items-center tw:rounded-md tw:border tw:px-2 tw:py-1.5 tw:text-left tw:text-xs tw:hover:bg-accent',
        isDragging && 'tw:opacity-40'
      )}
      {...listeners}
      {...attributes}
    >
      {aorText(SITSTAT_WIDGET_TITLES[id])}
    </button>
  );
}

function AddWidgetMenu({
  unused,
  onAdd,
  templates = [],
  activeTemplateId = null,
  onLoadTemplate,
  onSaveTemplate,
}: {
  unused: SitStatWidgetId[];
  onAdd: (id: SitStatWidgetId) => void;
  templates?: SitStatTemplate[];
  activeTemplateId?: string | null;
  onLoadTemplate?: (template: SitStatTemplate) => void;
  onSaveTemplate?: (name: string) => Promise<void> | void;
}) {
  const aorText = useAorText();
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [widgetQuery, setWidgetQuery] = useState('');

  const filteredUnused = useMemo(() => {
    const query = widgetQuery.trim().toLowerCase();
    if (!query) return unused;
    return unused.filter((id) =>
      aorText(SITSTAT_WIDGET_TITLES[id]).toLowerCase().includes(query)
    );
  }, [unused, widgetQuery, aorText]);

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) setWidgetQuery('');
        }}
      >
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tw:h-7 tw:gap-1 tw:px-2 tw:text-[11px]"
            >
              <Plus className="tw:h-3.5 tw:w-3.5" />
              Add Widget
            </Button>
          }
        />
        <PopoverContent align="start" className="dark tw:w-72 tw:p-2">
          <div className="tw:space-y-3">
            {onLoadTemplate || onSaveTemplate ? (
              <div className="tw:space-y-1">
                <p
                  data-tour="sitstat-views"
                  className="tw:px-1 tw:pb-1 tw:text-[10px] tw:font-medium tw:uppercase tw:tracking-wide tw:text-muted-foreground"
                >
                  Views
                </p>
                {templates.length === 0 ? (
                  <p className="tw:px-1 tw:text-xs tw:text-muted-foreground">
                    No saved views yet.
                  </p>
                ) : (
                  <div className="tw:max-h-28 tw:space-y-1 tw:overflow-y-auto">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className="tw:flex tw:items-center tw:gap-1"
                      >
                        <span
                          className={cn(
                            'tw:min-w-0 tw:flex-1 tw:truncate tw:px-1 tw:text-xs',
                            activeTemplateId === template.id &&
                              'tw:font-semibold tw:text-primary'
                          )}
                        >
                          {template.name}
                        </span>
                        {onLoadTemplate ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="tw:h-auto tw:shrink-0 tw:px-2 tw:text-[11px]"
                            onClick={() => {
                              onLoadTemplate(template);
                              setOpen(false);
                            }}
                          >
                            Load
                          </Button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
                {onSaveTemplate ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="tw:mt-1 tw:h-7 tw:w-full tw:text-[11px]"
                    onClick={() => {
                      setSaveName('');
                      setSaveOpen(true);
                      setOpen(false);
                    }}
                  >
                    Save current as view…
                  </Button>
                ) : null}
              </div>
            ) : null}

            <div className="tw:space-y-1.5">
              <p className="tw:px-1 tw:text-[10px] tw:font-medium tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                Widgets
              </p>
              {unused.length === 0 ? (
                <p className="tw:px-1 tw:py-2 tw:text-xs tw:text-muted-foreground">
                  All widgets are on the board.
                </p>
              ) : (
                <>
                  <div className="tw:relative">
                    <Search className="tw:pointer-events-none tw:absolute tw:left-2 tw:top-1/2 tw:h-3 tw:w-3 tw:-translate-y-1/2 tw:text-muted-foreground" />
                    <Input
                      value={widgetQuery}
                      onChange={(event) => setWidgetQuery(event.target.value)}
                      placeholder="Search widgets…"
                      className="tw:h-7 tw:pl-7 tw:text-xs"
                      aria-label="Search widgets"
                    />
                  </div>
                  <div className="tw:max-h-56 tw:space-y-1 tw:overflow-y-auto tw:overscroll-contain tw:pr-0.5">
                    {filteredUnused.length === 0 ? (
                      <p className="tw:px-1 tw:py-2 tw:text-xs tw:text-muted-foreground">
                        No matching widgets.
                      </p>
                    ) : (
                      filteredUnused.map((id) => (
                        <div key={id} className="tw:flex tw:gap-1">
                          <div className="tw:min-w-0 tw:flex-1">
                            <PaletteDraggableItem id={id} />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="tw:h-auto tw:shrink-0 tw:px-2 tw:text-[11px]"
                            onClick={() => {
                              onAdd(id);
                              setOpen(false);
                            }}
                          >
                            Add
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="dark tw:sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save SitStat view</DialogTitle>
            <DialogDescription>
              Saves the current widget layout and rail collapse state for this
              Hub.
            </DialogDescription>
          </DialogHeader>
          <div className="tw:grid tw:gap-2 tw:py-2">
            <Label htmlFor="sitstat-view-name">View name</Label>
              <Input
              id="sitstat-view-name"
              value={saveName}
              onChange={(event) => setSaveName(event.target.value)}
              placeholder="e.g. Duty Officer Watch"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSaveOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isSaving || !saveName.trim() || !onSaveTemplate}
              onClick={() => {
                if (!onSaveTemplate) return;
                setIsSaving(true);
                void Promise.resolve(onSaveTemplate(saveName.trim()))
                  .then(() => {
                    setSaveOpen(false);
                    setSaveName('');
                  })
                  .finally(() => setIsSaving(false));
              }}
            >
              Save view
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RailDropZone({
  railId,
  ids,
  renderWidget,
  onRemove,
}: {
  railId: RailId;
  ids: SitStatWidgetId[];
  renderWidget: (id: SitStatWidgetId) => ReactNode;
  onRemove: (id: SitStatWidgetId) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: railId });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3 tw:overflow-y-auto tw:p-3',
        isOver && 'tw:bg-primary/5'
      )}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {ids.length === 0 ? (
          <div className="tw:rounded-md tw:border tw:border-dashed tw:px-3 tw:py-8 tw:text-center tw:text-xs tw:text-muted-foreground">
            Drop widgets here
          </div>
        ) : (
          ids.map((id) => (
            <SortableSitStatWidget
              key={id}
              id={id}
              onRemove={() => onRemove(id)}
            >
              {renderWidget(id)}
            </SortableSitStatWidget>
          ))
        )}
      </SortableContext>
    </div>
  );
}

export function SitStatSortableGrid({
  layout,
  onLayoutChange,
  leftCollapsed,
  rightCollapsed,
  onLeftCollapsedChange,
  onRightCollapsedChange,
  onExitSitStat,
  renderWidget,
  templates = [],
  activeTemplateId = null,
  onLoadTemplate,
  onSaveTemplate,
  mapSlot,
  className,
}: SitStatSortableGridProps) {
  const aorText = useAorText();
  const [activeId, setActiveId] = useState<SitStatWidgetId | null>(null);
  const [activeFromPalette, setActiveFromPalette] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const leftIds = layout.left;
  const rightIds = layout.right;
  const unused = useMemo(() => getUnusedSitStatWidgets(layout), [layout]);

  const activeTitle = useMemo(
    () => (activeId ? aorText(SITSTAT_WIDGET_TITLES[activeId]) : null),
    [activeId, aorText]
  );

  const handleRemove = (id: SitStatWidgetId) => {
    onLayoutChange({
      ...layout,
      center: [],
      left: layout.left.filter((entry) => entry !== id),
      right: layout.right.filter((entry) => entry !== id),
    });
  };

  const handleAddToRail = (rail: RailId, id: SitStatWidgetId) => {
    if (layout.left.includes(id) || layout.right.includes(id)) return;
    onLayoutChange({
      ...layout,
      center: [],
      [rail]: [id, ...layout[rail]],
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    const paletteId = parsePaletteDragId(id);
    if (paletteId) {
      setActiveId(paletteId);
      setActiveFromPalette(true);
      return;
    }
    if (isSitStatWidgetId(id)) {
      setActiveId(id);
      setActiveFromPalette(false);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || activeFromPalette) return;

    const activeWidgetId = String(active.id);
    if (!isSitStatWidgetId(activeWidgetId)) return;

    const fromRail = findRail(layout, activeWidgetId);
    const toRail = findRail(layout, String(over.id));
    if (!fromRail || !toRail || fromRail === toRail) return;

    const fromItems = [...layout[fromRail]];
    const toItems = [...layout[toRail]];
    const fromIndex = fromItems.indexOf(activeWidgetId);
    if (fromIndex < 0) return;

    fromItems.splice(fromIndex, 1);

    const overId = String(over.id);
    let toIndex = toItems.length;
    if (isSitStatWidgetId(overId)) {
      const overIndex = toItems.indexOf(overId);
      toIndex = overIndex >= 0 ? overIndex : toItems.length;
    }

    toItems.splice(toIndex, 0, activeWidgetId);

    onLayoutChange({
      ...layout,
      center: [],
      [fromRail]: fromItems,
      [toRail]: toItems,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const wasFromPalette = activeFromPalette;
    setActiveId(null);
    setActiveFromPalette(false);
    if (!over) return;

    const overId = String(over.id);
    const toRail = findRail(layout, overId);
    if (!toRail) return;

    const paletteId = parsePaletteDragId(String(active.id));
    if (wasFromPalette && paletteId) {
      handleAddToRail(toRail, paletteId);
      return;
    }

    const activeWidgetId = String(active.id);
    if (!isSitStatWidgetId(activeWidgetId)) return;

    const fromRail = findRail(layout, activeWidgetId);
    if (!fromRail) return;

    if (fromRail === toRail) {
      const items = layout[fromRail];
      const fromIndex = items.indexOf(activeWidgetId);
      const toIndex = isSitStatWidgetId(overId)
        ? items.indexOf(overId)
        : items.length - 1;
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
      onLayoutChange({
        ...layout,
        center: [],
        [fromRail]: arrayMove(items, fromIndex, toIndex),
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setActiveId(null);
        setActiveFromPalette(false);
      }}
    >
      <SitStatRailsShell
        className={className}
        mapSlot={mapSlot}
        leftCollapsed={leftCollapsed}
        rightCollapsed={rightCollapsed}
        onLeftCollapsedChange={onLeftCollapsedChange}
        onRightCollapsedChange={onRightCollapsedChange}
        onExitSitStat={onExitSitStat}
        leftActions={
          <AddWidgetMenu
            unused={unused}
            onAdd={(id) => handleAddToRail('left', id)}
            templates={templates}
            activeTemplateId={activeTemplateId}
            onLoadTemplate={onLoadTemplate}
            onSaveTemplate={onSaveTemplate}
          />
        }
        rightActions={
          <AddWidgetMenu
            unused={unused}
            onAdd={(id) => handleAddToRail('right', id)}
            templates={templates}
            activeTemplateId={activeTemplateId}
            onLoadTemplate={onLoadTemplate}
            onSaveTemplate={onSaveTemplate}
          />
        }
        leftContent={
          <RailDropZone
            railId="left"
            ids={leftIds}
            renderWidget={renderWidget}
            onRemove={handleRemove}
          />
        }
        rightContent={
          <RailDropZone
            railId="right"
            ids={rightIds}
            renderWidget={renderWidget}
            onRemove={handleRemove}
          />
        }
      />

      <DragOverlay>
        {activeId ? (
          <div className="dark tw:w-[276px]">
            <SitStatWidgetCard
              title={activeTitle ?? ''}
              dragHandle={
                <SitStatDragHandle
                  ariaLabel={`Dragging ${activeTitle ?? 'widget'}`}
                  isDragging
                />
              }
            >
              <div className="tw:text-xs tw:text-muted-foreground">
                {activeFromPalette ? 'Add widget…' : 'Moving widget…'}
              </div>
            </SitStatWidgetCard>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
